/**
 * Tool icons.
 *
 * The `tools` table stores an icon *key*, not markup or a URL — so adding a tool
 * is a database row, and the SVG that renders it is reviewed code rather than
 * data. An unknown key falls back to a neutral square instead of rendering
 * nothing, so a new tool is always visible even before its icon is drawn.
 */

const ICONS: Record<string, React.ReactNode> = {
  contacts: (
    <>
      <path d="M16 19v-1.6a3.4 3.4 0 0 0-3.4-3.4H6.4A3.4 3.4 0 0 0 3 17.4V19" />
      <circle cx="9.5" cy="7.5" r="3.5" />
      <path d="M21 19v-1.6a3.4 3.4 0 0 0-2.6-3.3M15.5 4.2a3.5 3.5 0 0 1 0 6.6" />
    </>
  ),
  shield: (
    <>
      <path d="M12 21s7-3.2 7-9V5.6L12 3 5 5.6V12c0 5.8 7 9 7 9z" />
      <path d="M9.2 12.2l2 2 3.6-3.8" />
    </>
  ),
  grid: (
    <>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.2" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.2" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.2" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.2" />
    </>
  ),
  box: (
    <>
      <path d="M20.5 7.8v8.4L12 21l-8.5-4.8V7.8L12 3z" />
      <path d="M3.5 7.8L12 12.6l8.5-4.8M12 12.6V21" />
    </>
  ),
  orders: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="1.5" />
      <path d="M9 3.5h6v3H9zM8.5 10h7M8.5 14h7M8.5 18h4" />
      <path d="m15.5 17 1.2 1.2 2.4-2.6" />
    </>
  ),
  accounting: (
    <>
      <rect x="5" y="2.8" width="14" height="18.4" rx="1.5" />
      <path d="M8.5 6.7h7M8.5 10.5h.01M12 10.5h.01M15.5 10.5h.01M8.5 14.2h.01M12 14.2h.01M15.5 14.2h.01M8.5 18h7" />
    </>
  ),
  inventory: (
    <>
      <path d="M4 8.2 12 4l8 4.2v8.4L12 21l-8-4.4z" />
      <path d="m4 8.2 8 4.4 8-4.4M12 12.6V21M8.4 6l8 4.2" />
      <path d="M8.5 15.4h3" />
    </>
  ),
  products: (
    <>
      <path d="M4 8.2 12 4l8 4.2v8.4L12 21l-8-4.4z" />
      <path d="m4 8.2 8 4.4 8-4.4M12 12.6V21" />
      <path d="M8.4 6.1 12 8l3.6-1.9" />
    </>
  ),
  pricing: (
    <>
      <path d="M5 5.2h9.3L20 10.9l-8.8 8.8L5 13.9z" />
      <circle cx="9.4" cy="9.5" r="1.25" />
      <path d="M14.4 13.8h.01M17 16.4h.01" />
    </>
  ),
};

export function ToolIcon({ name }: { name: string | null }) {
  const glyph = (name && ICONS[name]) || ICONS.box;
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {glyph}
    </svg>
  );
}
